/**
 * Automatic Discount Detector for Shopify
 * ========================================
 * Automatically detects and displays strike-through pricing for products
 * with active Shopify automatic discounts (collection, product, or order-based).
 *
 * Features:
 * - Works with ALL Shopify automatic discounts
 * - No manual configuration needed
 * - Detects discounts on product pages and product cards
 * - Shows strike-through pricing automatically
 * - Caches results to minimize API calls
 * - ✅ FIXED v1.0.3: Prevents race condition when adding to cart
 * - ✅ FIXED: Skips currently selected variant on product pages
 * - ✅ FIXED: Hooks into add-to-cart to protect new items
 *
 * Version: 1.0.3
 */

class AutomaticDiscountDetector {
  constructor() {
    this.cache = new Map();
    this.processing = new Set();
    this.userCartVariants = new Set(); // Track what's in user's cart
    this.protectedVariants = new Set(); // Variants being added by user (race protection)
    this.isProductPage = false;
    this.currentProductVariantId = null;
    this.init();
  }

  init() {
    // Detect if we're on a product page
    this.isProductPage = this.detectProductPage();
    
    // Wait for DOM to be ready, then check once
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeAndDetect();
      });
    } else {
      // DOM already loaded, check immediately
      this.initializeAndDetect();
    }

    // Re-check when AJAX content loads (for quick view, infinite scroll, etc.)
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => this.detectDiscounts(), 500);
    });

    // Listen for variant changes on product pages
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.currentProductVariantId = String(e.detail.variant.id);
        // Don't auto-check - user might be about to add to cart
      }
    });

    // Watch for dynamically loaded products (lazy loading, infinite scroll, AJAX)
    this.observeNewProducts();
    
    // ✅ Hook into add-to-cart to prevent race conditions
    this.interceptAddToCart();
  }

  /**
   * Detect if we're on a product page
   */
  detectProductPage() {
    // Check URL pattern
    if (window.location.pathname.includes('/products/')) {
      return true;
    }
    // Check for product form
    if (document.querySelector('form[action*="/cart/add"] input[name="id"]')) {
      return true;
    }
    return false;
  }

  /**
   * Get currently selected variant ID on product page
   */
  getCurrentVariantId() {
    const variantInput = document.querySelector('input[name="id"], select[name="id"]');
    if (variantInput) {
      return String(variantInput.value);
    }
    return null;
  }

  /**
   * ✅ Intercept add-to-cart requests to prevent race conditions
   */
  interceptAddToCart() {
    // Store original fetch
    const originalFetch = window.fetch;
    const self = this;

    // Override fetch to detect cart additions
    window.fetch = function(url, options) {
      // Check if this is an add-to-cart request
      if (url && url.includes('/cart/add')) {
        try {
          const body = options?.body;
          if (body) {
            let data;
            if (typeof body === 'string') {
              data = JSON.parse(body);
            } else if (body instanceof FormData) {
              data = { id: body.get('id') };
            }
            
            // Protect this variant from discount detector interference
            if (data?.id) {
              self.protectedVariants.add(String(data.id));
              console.log(`[Discount Detector] 🛡️ Protecting variant ${data.id} during add-to-cart`);
            }
            if (data?.items) {
              data.items.forEach(item => {
                if (item.id) {
                  self.protectedVariants.add(String(item.id));
                  console.log(`[Discount Detector] 🛡️ Protecting variant ${item.id} during add-to-cart`);
                }
              });
            }
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      // Call original fetch
      return originalFetch.apply(this, arguments).then(response => {
        // After successful cart add, update our cart state
        if (url && url.includes('/cart/add') && response.ok) {
          // Wait a bit then refresh cart state
          setTimeout(() => {
            self.refreshUserCart();
          }, 300);
        }
        return response;
      });
    };

    // Also intercept form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        const variantInput = form.querySelector('[name="id"]');
        if (variantInput) {
          self.protectedVariants.add(String(variantInput.value));
          console.log(`[Discount Detector] 🛡️ Protecting variant ${variantInput.value} during form submit`);
          
          // Refresh cart state after form submits
          setTimeout(() => {
            self.refreshUserCart();
          }, 500);
        }
      }
    }, true);

    // Intercept click on add-to-cart buttons
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button[type="submit"], button[name="add"], .add-to-cart, [data-add-to-cart]');
      if (button) {
        const form = button.closest('form');
        if (form && form.action && form.action.includes('/cart')) {
          const variantInput = form.querySelector('[name="id"]');
          if (variantInput) {
            self.protectedVariants.add(String(variantInput.value));
            console.log(`[Discount Detector] 🛡️ Protecting variant ${variantInput.value} on button click`);
          }
        }
      }
    }, true);
  }

  /**
   * Initialize by capturing current cart state, then detect discounts
   */
  async initializeAndDetect() {
    // Get current variant on product page
    if (this.isProductPage) {
      this.currentProductVariantId = this.getCurrentVariantId();
      console.log(`[Discount Detector] 📍 On product page, current variant: ${this.currentProductVariantId}`);
    }
    
    // ✅ CRITICAL: Capture what's in the user's cart BEFORE we start
    await this.captureUserCart();
    
    // Add a small delay on product pages to avoid race conditions
    if (this.isProductPage) {
      setTimeout(() => this.detectDiscounts(), 300);
    } else {
      this.detectDiscounts();
    }
  }

  /**
   * Capture current cart state - these items should NEVER be touched
   */
  async captureUserCart() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      this.userCartVariants.clear();
      
      cart.items.forEach(item => {
        this.userCartVariants.add(String(item.variant_id));
      });
      
      // Also add protected variants to cart variants
      this.protectedVariants.forEach(v => this.userCartVariants.add(v));
      
      if (this.userCartVariants.size > 0) {
        console.log(`[Discount Detector] 🛒 Protected variants: ${Array.from(this.userCartVariants).join(', ')}`);
      }
    } catch (error) {
      console.error('[Discount Detector] Failed to capture cart state:', error);
    }
  }

  /**
   * Refresh user cart state (call after user adds items)
   */
  async refreshUserCart() {
    await this.captureUserCart();
    console.log(`[Discount Detector] 🔄 Refreshed cart state`);
  }

  /**
   * Observe DOM for newly added products (lazy loading, infinite scroll)
   */
  observeNewProducts() {
    const observer = new MutationObserver((mutations) => {
      let hasNewProducts = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node contains price elements
            const hasPriceElements =
              node.querySelector &&
              node.querySelector('[data-product-id][data-variant-id]:not([data-discount-checked])');

            if (hasPriceElements) {
              hasNewProducts = true;
            }
          }
        });
      });

      if (hasNewProducts) {
        // Debounce: quick check with batch processing
        clearTimeout(this.mutationTimeout);
        this.mutationTimeout = setTimeout(() => {
          this.detectDiscounts();
        }, 500); // Increased delay for safety
      }
    });

    // Observe the entire body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Check if a variant should be skipped (protected)
   */
  shouldSkipVariant(variantId) {
    const vid = String(variantId);
    
    // Skip if in user's cart
    if (this.userCartVariants.has(vid)) {
      return 'in cart';
    }
    
    // Skip if protected (being added)
    if (this.protectedVariants.has(vid)) {
      return 'protected';
    }
    
    // Skip if it's the current variant on product page
    if (this.isProductPage && this.currentProductVariantId === vid) {
      return 'current variant';
    }
    
    return false;
  }

  /**
   * Main function to detect discounts on all products visible on the page
   */
  async detectDiscounts() {
    // Update current variant (might have changed)
    if (this.isProductPage) {
      this.currentProductVariantId = this.getCurrentVariantId();
    }
    
    // Find all price containers with data attributes
    const priceElements = document.querySelectorAll('[data-product-id][data-variant-id]:not([data-discount-checked])');

    if (priceElements.length === 0) return;

    console.log(`[Discount Detector] 🔍 Found ${priceElements.length} products to check`);

    // Group by variant ID to avoid duplicate checks
    const variantIds = new Set();
    const elementsMap = new Map();
    let wpdSkipped = 0;
    let protectedSkipped = 0;

    priceElements.forEach(el => {
      const variantId = el.getAttribute('data-variant-id');
      const productId = el.getAttribute('data-product-id');

      // Skip if WPD (Wholesale Pricing Discount) is active on this element
      if (this.isWPDActive(el)) {
        el.setAttribute('data-discount-checked', 'true');
        el.setAttribute('data-wpd-discount-active', 'true');
        wpdSkipped++;
        return;
      }

      // ✅ Skip if this variant should be protected
      const skipReason = this.shouldSkipVariant(variantId);
      if (skipReason) {
        el.setAttribute('data-discount-checked', 'true');
        el.setAttribute('data-skip-reason', skipReason);
        protectedSkipped++;
        return;
      }

      if (variantId && productId) {
        variantIds.add(variantId);

        if (!elementsMap.has(variantId)) {
          elementsMap.set(variantId, []);
        }
        elementsMap.get(variantId).push(el);
      }
    });

    if (wpdSkipped > 0) {
      console.log(`[Discount Detector] ⏭️  Skipped ${wpdSkipped} products (WPD wholesale active)`);
    }
    
    if (protectedSkipped > 0) {
      console.log(`[Discount Detector] 🛡️ Skipped ${protectedSkipped} products (protected/in cart/current)`);
    }

    const allVariantIds = Array.from(variantIds);

    // If on product page and no variants to check, we're done
    if (allVariantIds.length === 0) {
      console.log(`[Discount Detector] ✅ All products already checked or protected`);
      return;
    }

    // HYBRID APPROACH: Verify first 1 products, calculate rest instantly
    const MAX_VERIFIED = 1; // Check first 1 via Cart API for accuracy

    if (allVariantIds.length > 0) {
      // Step 1: Verify first 1 product via Cart API (most accurate)
      const verifyIds = allVariantIds.slice(0, MAX_VERIFIED);
      console.log(`[Discount Detector] ⚡ Verifying first ${verifyIds.length} products via Cart API...`);

      await this.checkVariantDiscountsBatch(verifyIds, elementsMap);

      // Step 2: Check if we found a discount percentage
      const detectedDiscount = this.getDetectedDiscountPercentage();

      // Step 3: If we have remaining products and found a discount, calculate instantly
      if (allVariantIds.length > MAX_VERIFIED && detectedDiscount > 0) {
        const calculateIds = allVariantIds.slice(MAX_VERIFIED);
        console.log(`[Discount Detector] 💨 Calculating ${calculateIds.length} remaining products instantly (${detectedDiscount}% off)...`);

        this.applyCalculatedDiscounts(calculateIds, elementsMap, detectedDiscount);
      } else if (allVariantIds.length > MAX_VERIFIED && detectedDiscount === 0) {
        // No discount found in first 1, mark rest as no discount
        console.log(`[Discount Detector] ⚪ No discount detected, skipping remaining ${allVariantIds.length - MAX_VERIFIED} products`);
        const remainingIds = allVariantIds.slice(MAX_VERIFIED);
        remainingIds.forEach(variantId => {
          const elements = elementsMap.get(variantId);
          elements.forEach(el => el.setAttribute('data-discount-checked', 'true'));
        });
      }
    }
  }

  /**
   * Get the discount percentage detected from verified products
   */
  getDetectedDiscountPercentage() {
    // Look through cache to find discount percentage
    for (const [variantId, data] of this.cache.entries()) {
      if (data.hasDiscount && data.discountPercentage > 0) {
        return data.discountPercentage;
      }
    }
    return 0;
  }

  /**
   * Apply calculated discounts instantly (client-side calculation)
   */
  applyCalculatedDiscounts(variantIds, elementsMap, discountPercentage) {
    variantIds.forEach(variantId => {
      const elements = elementsMap.get(variantId);
      if (!elements || elements.length === 0) return;

      // Mark as checked
      elements.forEach(el => el.setAttribute('data-discount-checked', 'true'));

      // Get current price from element
      const currentPrice = parseInt(elements[0].getAttribute('data-variant-price'));

      // Calculate discount
      const discountDecimal = discountPercentage / 100;
      const discountAmount = Math.round(currentPrice * discountDecimal);
      const discountedPrice = currentPrice - discountAmount;

      const discountData = {
        hasDiscount: true,
        originalPrice: currentPrice,
        discountedPrice: discountedPrice,
        discountPercentage: discountPercentage,
        discountAmount: discountAmount,
        calculated: true // Mark as calculated (not verified via Cart API)
      };

      // Cache the result
      this.cache.set(variantId, discountData);

      // Update display
      this.updatePriceDisplay(elements, discountData);

      console.log(`[Discount Detector] 💨 Variant ${variantId}: ${discountPercentage}% off (calculated: ${this.formatMoney(currentPrice)} → ${this.formatMoney(discountedPrice)})`);
    });

    console.log(`[Discount Detector] ✅ Instantly calculated ${variantIds.length} products!`);
  }

  /**
   * Check if WPD (Wholesale Pricing Discount) app is active for this element
   * WPD modifies prices for logged-in wholesale customers based on tags
   */
  isWPDActive(element) {
    // Check if element or its children have WPD data attributes
    const priceContainer = element.closest('.price');
    if (!priceContainer) return false;

    // Method 1: Check for wpd-processed class (WPD adds this when it modifies prices)
    const wpdProcessedElements = priceContainer.querySelectorAll('.wpd-processed');
    if (wpdProcessedElements.length > 0) {
      return true; // WPD has processed this price
    }

    // Method 2: Check for wpd-ext-compare-at-price class (WPD's strikethrough)
    const wpdComparePrice = priceContainer.querySelector('.wpd-ext-compare-at-price');
    if (wpdComparePrice) {
      return true; // WPD is showing wholesale discount
    }

    // Method 3: Check for data-wpd-hide class (hidden elements by WPD)
    const wpdHiddenElements = priceContainer.querySelectorAll('.data-wpd-hide');
    if (wpdHiddenElements.length > 0) {
      for (const hiddenEl of wpdHiddenElements) {
        const computedStyle = window.getComputedStyle(hiddenEl);
        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
          return true; // WPD is active
        }
      }
    }

    // Method 4: Check if WPD has modified the price display
    // WPD adds strike-through and new price in the same element
    const priceElements = priceContainer.querySelectorAll('[data-wpd-variant-id]');
    for (const priceEl of priceElements) {
      const hasStrikethrough = priceEl.querySelector('s.wpd-ext-compare-at-price');
      if (hasStrikethrough) {
        return true; // WPD is active
      }
    }

    return false; // WPD is not active
  }

  /**
   * Sequential check - one product at a time to prevent cart conflicts
   * This is the most reliable approach for Shopify's cart API
   */
  async checkVariantDiscountsBatch(variantIds, elementsMap) {
    if (variantIds.length === 0) return;

    const startTime = performance.now();

    // Process products ONE AT A TIME (sequential, not parallel)
    // This prevents ALL cart conflicts
    for (let i = 0; i < variantIds.length; i++) {
      const variantId = variantIds[i];
      
      // Double-check protection before each check
      if (this.shouldSkipVariant(variantId)) {
        console.log(`[Discount Detector] 🛡️ Skipping ${variantId} - protected`);
        continue;
      }
      
      await this.checkVariantDiscountFast(variantId, elementsMap.get(variantId));
      // Small delay between products for cart stability (except after last product)
      if (i < variantIds.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    const endTime = performance.now();
    console.log(`[Discount Detector] ✅ Verified ${variantIds.length} products via Cart API in ${Math.round(endTime - startTime)}ms`);
  }

  /**
   * Sequential variant check (one at a time)
   * ✅ FIXED v1.0.3: Multiple layers of protection against race conditions
   */
  async checkVariantDiscountFast(variantId, elements, retryCount = 0) {
    if (!elements || elements.length === 0) return;

    // ✅ TRIPLE CHECK: Never touch protected variants
    const skipReason = this.shouldSkipVariant(variantId);
    if (skipReason) {
      console.log(`[Discount Detector] 🛡️ Skipping variant ${variantId} - ${skipReason}`);
      elements.forEach(el => el.setAttribute('data-discount-checked', 'true'));
      return;
    }

    // Mark as checked
    elements.forEach(el => el.setAttribute('data-discount-checked', 'true'));

    // Check cache first
    if (this.cache.has(variantId)) {
      const discountData = this.cache.get(variantId);
      this.updatePriceDisplay(elements, discountData);
      return;
    }

    // Get current price from element
    const currentPrice = parseInt(elements[0].getAttribute('data-variant-price'));

    try {
      // ✅ BEFORE adding: Double-check cart state and get accurate original quantity
      const cartBefore = await fetch('/cart.js').then(r => r.json());
      const existingItem = cartBefore.items.find(item => String(item.variant_id) === String(variantId));
      
      // If item appeared in cart while we were processing, skip it!
      if (existingItem) {
        console.log(`[Discount Detector] 🛡️ Variant ${variantId} appeared in cart, skipping`);
        this.userCartVariants.add(String(variantId));
        return;
      }

      // Add item to cart to check discount
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      // Handle different response statuses
      if (!addResponse.ok) {
        const errorText = await addResponse.text();

        // ✅ Handle out of stock
        if (errorText.includes('out of stock') || errorText.includes('sold out')) {
          console.log(`[Discount Detector] ⏭️  Variant ${variantId}: Out of stock, skipping`);
          return;
        }

        // ✅ Handle max quantity errors - don't retry, just skip
        if (errorText.includes('maximum quantity') || errorText.includes('not enough') || errorText.includes('inventory')) {
          console.log(`[Discount Detector] ⏭️  Variant ${variantId}: Inventory limit, skipping`);
          return;
        }

        // Retry on 409 conflicts (should be rare with sequential processing)
        if (retryCount < 1 && errorText.includes('409')) {
          console.log(`[Discount Detector] 🔄 Variant ${variantId}: Cart conflict, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 300));
          return this.checkVariantDiscountFast(variantId, elements, retryCount + 1);
        }

        throw new Error(`Failed to add to cart: ${errorText}`);
      }

      // Get updated cart with discount info
      const updatedCart = await fetch('/cart.js').then(r => r.json());

      // Find the item we just added
      const addedItem = updatedCart.items.find(item => String(item.variant_id) === String(variantId));

      let discountData = {
        hasDiscount: false,
        originalPrice: currentPrice,
        discountedPrice: currentPrice,
        discountPercentage: 0
      };

      if (addedItem) {
        // Check if there's a discount applied
        if (addedItem.original_price && addedItem.final_price &&
            addedItem.original_price > addedItem.final_price) {

          const discountAmount = addedItem.original_price - addedItem.final_price;
          const discountPercentage = Math.round((discountAmount / addedItem.original_price) * 100);

          discountData = {
            hasDiscount: true,
            originalPrice: addedItem.original_price,
            discountedPrice: addedItem.final_price,
            discountPercentage: discountPercentage,
            discountAmount: discountAmount
          };

          console.log(`[Discount Detector] 💰 Variant ${variantId}: ${discountData.discountPercentage}% off (${this.formatMoney(discountData.originalPrice)} → ${this.formatMoney(discountData.discountedPrice)})`);
        } else {
          console.log(`[Discount Detector] ⚪ Variant ${variantId}: No discount`);
        }
      }

      // Cache the result
      this.cache.set(variantId, discountData);

      // Update display
      this.updatePriceDisplay(elements, discountData);

      // ✅ CLEANUP: Check cart AGAIN before removing
      const cartAfterCheck = await fetch('/cart.js').then(r => r.json());
      const itemNow = cartAfterCheck.items.find(item => String(item.variant_id) === String(variantId));
      
      if (itemNow) {
        // Only remove if quantity is exactly 1 (what we added)
        // If quantity > 1, user added something in the meantime!
        if (itemNow.quantity === 1) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              updates: {
                [variantId]: 0
              }
            })
          }).catch(() => {});
        } else {
          // User added this item! Subtract only 1 (what we added)
          console.log(`[Discount Detector] 🛡️ User added variant ${variantId} during check, restoring their quantity`);
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              updates: {
                [variantId]: itemNow.quantity - 1
              }
            })
          }).catch(() => {});
          
          // Protect this variant from future checks
          this.userCartVariants.add(String(variantId));
        }
      }

    } catch (error) {
      console.error(`[Discount Detector] ❌ Error checking variant ${variantId}:`, error.message);

      // Retry on cart conflicts only (should be very rare now)
      if (retryCount < 1 && error.message.includes('409')) {
        console.log(`[Discount Detector] 🔄 Variant ${variantId}: Retrying...`);
        await new Promise(resolve => setTimeout(resolve, 300));
        return this.checkVariantDiscountFast(variantId, elements, retryCount + 1);
      }
    }
  }

  /**
   * Check single variant (for variant selector changes)
   */
  async checkSingleVariant(variantId) {
    // ✅ Never check variants that should be skipped
    if (this.shouldSkipVariant(variantId)) {
      return;
    }
    
    const elements = document.querySelectorAll(`[data-variant-id="${variantId}"]`);
    if (elements.length > 0) {
      elements.forEach(el => el.removeAttribute('data-discount-checked'));
      await this.checkVariantDiscountFast(variantId, Array.from(elements));
    }
  }

  /**
   * Update the price display with discount information
   */
  updatePriceDisplay(elements, discountData) {
    if (!discountData.hasDiscount) {
      return;
    }

    elements.forEach(element => {
      const priceContainer = element.closest('.price');
      if (!priceContainer) return;

      // Double-check WPD is not active (safety check)
      if (this.isWPDActive(element)) {
        element.setAttribute('data-wpd-discount-active', 'true');
        return;
      }

      // Check if already showing compare_at_price (manual sale)
      const hasManualSale = priceContainer.classList.contains('price-on-sale');
      if (hasManualSale) {
        // Don't override manual sales
        return;
      }

      // Format prices using Shopify money format
      const originalPriceFormatted = this.formatMoney(discountData.originalPrice);
      const discountedPriceFormatted = this.formatMoney(discountData.discountedPrice);

      // Add discount class
      priceContainer.classList.add('price-on-sale', 'price-has-automatic-discount');

      // Find or create price-sale container
      let salePriceContainer = priceContainer.querySelector('.price-sale');

      if (!salePriceContainer) {
        salePriceContainer = document.createElement('div');
        salePriceContainer.className = 'price-sale';

        const priceRegular = priceContainer.querySelector('.price-regular');
        if (priceRegular) {
          priceRegular.insertAdjacentElement('afterend', salePriceContainer);
        } else {
          priceContainer.querySelector('.price-container')?.appendChild(salePriceContainer);
        }
      }

      // Build the sale price HTML
      salePriceContainer.innerHTML = `
        <span class="visually-hidden">Regular price</span>
        <span>
          <s class="price-item price-item-regular" data-automatic-discount-original>
            ${originalPriceFormatted}
          </s>
        </span>
        <span class="visually-hidden">Sale price</span>
        <span class="price-item price-item-sale price-item-last" data-automatic-discount-final>
          ${discountedPriceFormatted}
        </span>
      `;

      // Update the regular price container to be hidden
      const regularPriceContainer = priceContainer.querySelector('.price-regular');
      if (regularPriceContainer) {
        regularPriceContainer.style.display = 'none';
      }

      // Add discount badge if configured
      if (window.automaticDiscountConfig && window.automaticDiscountConfig.showBadge) {
        this.addDiscountBadge(priceContainer, discountData.discountPercentage);
      }

      // Store discount data on element for reference
      element.setAttribute('data-discount-percentage', discountData.discountPercentage);
      element.setAttribute('data-has-automatic-discount', 'true');
    });
  }

  /**
   * Add discount badge to product
   */
  addDiscountBadge(priceContainer, percentage) {
    // Check if badge already exists
    if (priceContainer.querySelector('.badge-automatic-discount')) {
      return;
    }

    const badge = document.createElement('span');
    badge.className = 'badge badge-sale badge-automatic-discount';
    badge.textContent = `-${percentage}%`;

    const salePriceContainer = priceContainer.querySelector('.price-sale');
    if (salePriceContainer) {
      salePriceContainer.insertBefore(badge, salePriceContainer.firstChild);
    }
  }

  /**
   * Format money according to Shopify theme settings
   */
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }

    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Clear cache (useful for testing or when discounts change)
   */
  clearCache() {
    this.cache.clear();
  }
}

// Configuration object (optional)
window.automaticDiscountConfig = {
  showBadge: false, // Show discount percentage badge
  enableCache: true, // Cache discount checks
  debug: false // Enable console logging
};

// Initialize the detector
window.automaticDiscountDetector = new AutomaticDiscountDetector();

// Expose methods for manual triggering
window.refreshDiscountPricing = () => {
  window.automaticDiscountDetector.clearCache();
  document.querySelectorAll('[data-discount-checked]').forEach(el => {
    el.removeAttribute('data-discount-checked');
  });
  window.automaticDiscountDetector.detectDiscounts();
};

// Refresh cart state
window.refreshDiscountCartState = () => {
  window.automaticDiscountDetector.refreshUserCart();
};